import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession, useData } from '@yingbaba/api';
import { ActionDialog, PageTitle, Pagination, QueryState, Select } from '@yingbaba/ui';
import { datetime } from '@yingbaba/utils';
import type { BusinessEmailDeliveryPage } from '@yingbaba/types';

const labels: Record<string, string> = {
  pending: '等待发送',
  sending: '正在发送',
  accepted: '邮件服务已受理',
  failed: '发送失败',
  unknown: '投递结果未知',
  cancelled: '授权取消或提醒已过时',
};
const reasons: Record<string, string> = {
  CONSENT_OR_ACCESS_REVOKED: '用户授权、邮箱或访问权限已变化',
  EVENT_NO_LONGER_CURRENT: '业务状态已更新，旧提醒不再发送',
  SMTP_DELIVERY_UNKNOWN: '请先核查邮件服务记录，邮件可能已被受理',
  SMTP_TEMPORARY_REJECTION: '邮件服务暂时拒绝，将按计划重试',
  SMTP_PERMANENT_REJECTION: '邮件服务拒绝投递，请核对邮箱或发信配置',
  SMTP_CONNECTION_FAILED: '邮件服务连接失败',
  INVALID_MAIL_ADDRESS_OR_HEADERS: '邮箱地址或发信配置无效',
  INVALID_EMAIL_CONTENT: '邮件内容无效',
  DATABASE_TEMPORARILY_UNAVAILABLE: '投递记录暂时不可读取，将按计划重试',
};
export function BusinessEmailDeliveries() {
  const session = useSession();
  const canRetry = ['super_admin', 'operator'].includes(session.data?.account.platform_role || '');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const q = useData<BusinessEmailDeliveryPage>(
    `/admin/notification-email/deliveries?page=${page}&status=${status}`,
  );
  return (
    <>
      <PageTitle
        title="业务邮件投递"
        description="查看授权后的业务提醒。服务受理表示接受邮件，不代表用户已阅读。"
      />
      <Select
        className="input"
        aria-label="邮件投递状态"
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
      >
        <option value="">全部状态</option>
        {Object.entries(labels).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </Select>
      <QueryState query={q} empty={!q.data?.items.length}>
        <div className="panel" style={{ marginTop: 20 }}>
          {q.data?.items.map((row) => (
            <div className="list-item" key={row.id}>
              <div className="grow">
                <strong>{row.title}</strong>
                <p>
                  {labels[row.status] || row.status} · 尝试 {row.attempts} 次
                </p>
                <small className="muted">
                  {datetime(row.created_at)}
                  {row.last_code ? ` · ${reasons[row.last_code] || '请核查投递记录'}` : ''}
                </small>
                <p className="small muted">投递编号：{row.id}</p>
                {!row.event_available && (
                  <p className="small muted">
                    关联业务事件已不存在，仅保留历史投递记录，无法重发。
                  </p>
                )}
                <Link to={`/organizations/${row.organization_id}`} className="small">
                  查看所属组织
                </Link>
              </div>
              {canRetry && row.event_available && ['unknown', 'failed'].includes(row.status) ? (
                <ActionDialog
                  title="重新发送业务邮件"
                  submitLabel="确认重发"
                  trigger="重发邮件"
                  variant="secondary"
                  path={`/admin/notification-email/deliveries/${row.id}/retry`}
                  description={
                    row.status === 'unknown'
                      ? '邮件可能已被受理。请先核查邮件服务记录；重新发送可能产生重复邮件。'
                      : '重发前会再次核验用户授权、访问权限和当前业务状态。'
                  }
                  fields={[
                    {
                      name: 'reason',
                      label: '核查依据与重发原因',
                      required: true,
                      type: 'textarea',
                      min: 4,
                    },
                    ...(row.status === 'unknown'
                      ? [
                          {
                            name: 'confirmation',
                            label: '重复邮件风险',
                            required: true,
                            type: 'select',
                            options: [
                              { value: '', label: '请选择' },
                              { value: 'confirmed', label: '已核查，确认仍需重发' },
                            ],
                          },
                        ]
                      : []),
                  ]}
                  transform={(values) => ({
                    reason: values.reason,
                    allow_unknown: values.confirmation === 'confirmed',
                  })}
                />
              ) : null}
            </div>
          ))}
        </div>
      </QueryState>
      <Pagination page={page} total={q.data?.total || 0} size={30} onPage={setPage} />
    </>
  );
}
